// Builds ESC/POS line arrays for the companion app's printReceipt command.
// 80mm thermal printers fit ~42 chars per line at normal font size.

const W = 42;

const money = (v) => `Rs.${parseFloat(v || 0).toFixed(2)}`;
const pts   = (v) => `${parseFloat(v || 0).toFixed(2)} pts`;

function dateParts(value) {
  if (!value) return { date: '', time: '' };
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return { date: '', time: '' };
  return {
    date: d.toLocaleDateString('en-GB'),
    time: d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }),
  };
}

function twoCol(label, value, width = W) {
  const l = String(label || '');
  const v = String(value || '');
  const pad = Math.max(1, width - l.length - v.length);
  return l + ' '.repeat(pad) + v;
}

const txt  = (text, bold = false, align = 'left') => ({ type: 'text', text: String(text || ''), bold, align });
const bold = (text, align = 'left') => txt(text, true, align);
const sep  = () => ({ type: 'separator' });
const cut  = () => ({ type: 'cut' });
const feed = (count = 1) => ({ type: 'newline', count });

function wrapLines(text, width = W, indent = '', align = 'left') {
  const lines = [];
  let remaining = String(text || '');
  while (remaining.length > 0) {
    lines.push(txt(indent + remaining.substring(0, width - indent.length), false, align));
    remaining = remaining.substring(width - indent.length);
    indent = align === 'left' ? '  ' : ''; // subsequent lines indented only when left-aligned
  }
  return lines;
}

// Declarations + footer/website/instagram are shared across all three receipt
// types (they're org branding, not receipt-type-specific), so build them once.
function pushBrandingFooter(lines, receipt) {
  if (receipt.declarationText) {
    lines.push(sep());
    lines.push(bold('Declarations'));
    lines.push(...wrapLines(receipt.declarationText, W));
  }

  lines.push(sep());
  if (receipt.footerMessage) lines.push(...wrapLines(receipt.footerMessage, W, '', 'center'));
  if (receipt.websiteHandle) lines.push(txt(receipt.websiteHandle, false, 'center'));
  if (receipt.instagramHandle) lines.push(txt(receipt.instagramHandle, false, 'center'));
}

// ── Normal receipt ─────────────────────────────────────────────────────────────

export function buildReceiptLines(receipt) {
  const lines = [];
  const { date, time } = dateParts(receipt.createdAt);

  // Header
  // Note: receipt.logoUrl is intentionally not printed here -- see comment
  // above pushBrandingFooter / the PR description for why ESC-POS logo
  // rendering isn't implemented (companion app's print schema has no
  // image/raster line type to dither into; HTML and PDF receipts do show it).
  if (receipt.storeName)       lines.push({ type: 'text', text: receipt.storeName, bold: true, align: 'center', fontSize: 2 });
  if (receipt.organizationName) lines.push(txt(receipt.organizationName, false, 'center'));
  if (receipt.storeAddress)    lines.push(txt(receipt.storeAddress, false, 'center'));
  if (receipt.storePhone)      lines.push(txt(`Tel: ${receipt.storePhone}`, false, 'center'));
  if (receipt.storeEmail)      lines.push(txt(`Email: ${receipt.storeEmail}`, false, 'center'));
  if (receipt.gstin)           lines.push(txt(`GST: ${receipt.gstin}`, false, 'center'));

  lines.push(sep());
  lines.push(bold(receipt.invoiceTitle || 'TAX INVOICE', 'center'));
  lines.push(sep());

  // Meta
  lines.push(txt(twoCol('Bill No', receipt.billNumber || '')));
  lines.push(txt(twoCol('Date', date)));
  lines.push(txt(twoCol('Time', time)));
  if (receipt.cashierName) lines.push(txt(twoCol('Cashier', receipt.cashierName)));

  // Customer
  if (receipt.customerName || receipt.customerMobile) {
    lines.push(sep());
    if (receipt.customerName)   lines.push(txt(`Customer: ${receipt.customerName}`));
    if (receipt.customerMobile) lines.push(txt(`Mobile: ${receipt.customerMobile}`));
    if (receipt.customerAddress) lines.push(txt(`Addr: ${receipt.customerAddress}`.substring(0, W)));
  }

  // Items
  // Columns are joined with an explicit space, not just padStart/padEnd
  // targets, so a value that exactly fills its target width (e.g. a 4-digit
  // rate like "1695.00" is exactly 7 chars) can never butt up directly
  // against its neighbor with zero gap -- padStart alone doesn't guarantee
  // that once content reaches its target length.
  const showRate = receipt.showRateColumn !== false;
  const showAmt  = receipt.showAmountColumn !== false;
  const decimals = receipt.itemAmountDecimalPlaces || 2;
  const rateLabel = String(receipt.rateColumnLabel || 'Rate').substring(0, 7).padStart(7);
  const amtLabel  = String(receipt.amountColumnLabel || 'Amt').substring(0, 7).padStart(7);

  const itemRow = (idx, name, qty, rate, amt) =>
    [ idx + name, qty, rate, amt ].filter((part) => part !== null).join(' ');

  lines.push(sep());
  lines.push(txt(itemRow(
    'Sr'.padEnd(3), 'Item'.padEnd(18), 'Qty'.padStart(3),
    showRate ? rateLabel : null, showAmt ? amtLabel : null
  )));
  lines.push(sep());

  (receipt.items || []).forEach((item) => {
    const idx  = String(item.index).padEnd(3);
    const name = String(item.name).substring(0, 18).padEnd(18);
    const qty  = String(item.quantity).padStart(3);
    const rate = showRate ? parseFloat(item.rate || 0).toFixed(decimals).padStart(7) : null;
    const amt  = showAmt ? parseFloat(item.amount || 0).toFixed(decimals).padStart(7) : null;
    lines.push(txt(itemRow(idx, name, qty, rate, amt)));
    if (item.color || item.size) {
      const parts = [item.color, item.size].filter(Boolean);
      lines.push(txt(`   ${parts.join(' / ')}`));
    }
    if (item.hsn)  lines.push(txt(`   HSN: ${item.hsn}`));
    if (item.discountAmount > 0) {
      if (item.savingsMessage) {
        lines.push(txt(`   ${item.savingsMessage}`));
      } else {
        const label = item.discountLabel ? ` (${item.discountLabel})` : '';
        lines.push(txt(`   Disc${label}: ${money(item.discountAmount)}`));
      }
    }
  });

  // Totals
  lines.push(sep());
  lines.push(txt(twoCol('Subtotal', money(receipt.subtotal))));

  if (receipt.billDiscountAmount > 0) {
    const lbl = receipt.billDiscountLabel ? ` (${receipt.billDiscountLabel})` : '';
    lines.push(txt(twoCol(`Bill Disc${lbl}`, `-${money(receipt.billDiscountAmount)}`)));
  }
  if (receipt.billPromotionDiscountAmount > 0) {
    const lbl = receipt.billPromotionDiscountLabel ? ` (${receipt.billPromotionDiscountLabel})` : '';
    lines.push(txt(twoCol(`Promo${lbl}`, `-${money(receipt.billPromotionDiscountAmount)}`)));
  }
  if (receipt.discount > 0 && receipt.billDiscountAmount <= 0 && receipt.billPromotionDiscountAmount <= 0) {
    if (receipt.billSavingsMessage) {
      lines.push(txt(receipt.billSavingsMessage));
    } else {
      lines.push(txt(twoCol('Discount', `-${money(receipt.discount)}`)));
    }
  }
  if (receipt.roundingAdjustment !== 0) {
    const sign = receipt.roundingAdjustment > 0 ? '+' : '-';
    lines.push(txt(twoCol('Rounding', `${sign}${money(Math.abs(receipt.roundingAdjustment))}`)));
  }
  lines.push(bold(twoCol('TOTAL', money(receipt.totalAmount))));

  // Payments
  if ((receipt.payments || []).length > 0) {
    lines.push(sep());
    lines.push(bold('Payments'));
    receipt.payments.forEach((p) => {
      lines.push(txt(twoCol(p.label, money(p.amount))));
      if (p.referenceNumber) lines.push(txt(`  Ref: ${p.referenceNumber}`));
      if (p.note) lines.push(txt(`  ${p.note}`));
    });
    if (receipt.showTotalPaidRow) lines.push(bold(twoCol('Total Paid', money(receipt.totalPaid))));
    if (receipt.changeDue > 0) lines.push(txt(twoCol('Change Due', money(receipt.changeDue))));
  }

  // GST summary
  if ((receipt.gstSummary || []).length > 0) {
    lines.push(sep());
    lines.push(bold('GST Summary'));
    lines.push(txt('Tax       Taxable   CGST   SGST   IGST'));
    receipt.gstSummary.forEach((g) => {
      const tax    = String(g.label).substring(0, 9).padEnd(9);
      const taxbl  = parseFloat(g.taxableAmount || 0).toFixed(2).padStart(8);
      const cgst   = parseFloat(g.cgstAmount || 0).toFixed(2).padStart(7);
      const sgst   = parseFloat(g.sgstAmount || 0).toFixed(2).padStart(7);
      const igst   = parseFloat(g.igstAmount || 0).toFixed(2).padStart(7);
      lines.push(txt(`${tax}${taxbl}${cgst}${sgst}${igst}`));
    });
  }

  // Salesperson
  if (receipt.salespersonName) {
    lines.push(sep());
    lines.push(txt(`Salesperson: ${receipt.salespersonName}`));
  }

  // Terms
  if ((receipt.terms || []).length > 0) {
    lines.push(sep());
    lines.push(bold('Terms & Conditions'));
    receipt.terms.forEach((term, i) => {
      lines.push(...wrapLines(`${i + 1}. ${term}`, W));
    });
  }

  pushBrandingFooter(lines, receipt);
  lines.push(feed(3));
  lines.push(cut());

  return lines;
}

// ── Return / Credit Note ───────────────────────────────────────────────────────

export function buildReturnReceiptLines(receipt) {
  const lines = [];
  const { date, time } = dateParts(receipt.completedAt);

  // Header
  if (receipt.storeName) lines.push({ type: 'text', text: receipt.storeName, bold: true, align: 'center', fontSize: 2 });
  if (receipt.organizationName) lines.push(txt(receipt.organizationName, false, 'center'));
  if (receipt.storePhone) lines.push(txt(`Tel: ${receipt.storePhone}`, false, 'center'));
  if (receipt.gstin) lines.push(txt(`GST: ${receipt.gstin}`, false, 'center'));

  lines.push(sep());
  lines.push(bold(receipt.invoiceTitle || 'RETURN INVOICE', 'center'));
  lines.push(sep());

  lines.push(txt(twoCol('Return No', receipt.returnNumber || '')));
  if (receipt.creditNoteNumber) lines.push(txt(twoCol('Credit Note', receipt.creditNoteNumber)));
  lines.push(txt(twoCol('Date', date)));
  lines.push(txt(twoCol('Time', time)));
  lines.push(txt(twoCol('Orig Bill No', receipt.orderNumber || '')));
  if (receipt.refundMethod) lines.push(txt(twoCol('Refund via', receipt.refundMethod)));

  if (receipt.customerName || receipt.customerMobile) {
    lines.push(sep());
    if (receipt.customerName)   lines.push(txt(`Customer: ${receipt.customerName}`));
    if (receipt.customerMobile) lines.push(txt(`Mobile: ${receipt.customerMobile}`));
  }

  const returnShowRate = receipt.showRateColumn !== false;
  const returnDecimals = receipt.itemAmountDecimalPlaces || 2;
  const returnRateLabel = String(receipt.rateColumnLabel || 'Rate').substring(0, 6).padStart(6);

  lines.push(sep());
  lines.push(txt(`Sr Item              Reason     Qty${returnShowRate ? ` ${returnRateLabel}` : ''} Refund`));
  lines.push(sep());

  (receipt.items || []).forEach((item) => {
    const idx    = String(item.index).padEnd(3);
    const name   = String(item.name).substring(0, 14).padEnd(15);
    const reason = String(item.reason || '-').substring(0, 10).padEnd(11);
    const qty    = String(item.quantity).padStart(3);
    const rate   = returnShowRate ? parseFloat(item.rate || 0).toFixed(returnDecimals).padStart(6) : null;
    const refund = parseFloat(item.amount || 0).toFixed(returnDecimals).padStart(7);
    // Explicit separators, not just padStart's own width -- a rate that
    // exactly fills its target (e.g. "1695.00" is already 7 chars, wider
    // than the 6-char target) gets zero padding, so without a real space
    // here it runs straight into qty on one side and refund on the other.
    lines.push(txt([ `${idx}${name}${reason}${qty}`, rate, refund ].filter((part) => part !== null).join(' ')));
    if (item.hsn) lines.push(txt(`   HSN: ${item.hsn}`));
  });

  lines.push(sep());
  lines.push(bold(twoCol('TOTAL REFUND', money(receipt.refundAmount))));

  if (parseFloat(receipt.promotionClawbackTotal || 0) > 0) {
    lines.push(txt(twoCol('Promo Reversed', `-${money(receipt.promotionClawbackTotal)}`)));
    if (receipt.debitNoteNumber) lines.push(txt(twoCol('Debit Note', receipt.debitNoteNumber)));
    lines.push(txt(twoCol('Net Refund', money(receipt.monetaryRefundAmount))));
  }
  if ((receipt.settlementLines || []).length > 0) {
    // Where the refund went + the balances after it, so the customer can see
    // why store credit is less than the note when part was paid with points.
    lines.push(sep());
    lines.push(bold('Refund Settlement'));
    receipt.settlementLines.forEach((line) => {
      const label = line.tenderType === 'loyalty_points' && line.points != null
        ? `${line.label} (${pts(line.points)})`
        : line.label;
      lines.push(txt(twoCol(label, money(line.amount))));
    });
    if (receipt.storeCreditBalanceAfter != null) lines.push(txt(twoCol('Store Credit Balance', money(receipt.storeCreditBalanceAfter))));
    if (receipt.loyaltyPointsBalanceAfter != null) lines.push(txt(twoCol('Points Balance', pts(receipt.loyaltyPointsBalanceAfter))));
  } else if (parseFloat(receipt.loyaltyPointsRefunded || 0) > 0) {
    lines.push(txt(twoCol(`${receipt.refundMethod || 'Amount'} Refunded`, money(receipt.monetaryRefundAmount))));
    lines.push(txt(twoCol('Points Returned', pts(receipt.loyaltyPointsRefunded))));
  }

  if ((receipt.gstSummary || []).length > 0) {
    lines.push(sep());
    lines.push(bold('GST Summary'));
    lines.push(txt('Tax       Taxable   CGST   SGST   IGST'));
    receipt.gstSummary.forEach((g) => {
      const tax   = String(g.label).substring(0, 9).padEnd(9);
      const taxbl = parseFloat(g.taxableAmount || 0).toFixed(2).padStart(8);
      const cgst  = parseFloat(g.cgstAmount || 0).toFixed(2).padStart(7);
      const sgst  = parseFloat(g.sgstAmount || 0).toFixed(2).padStart(7);
      const igst  = parseFloat(g.igstAmount || 0).toFixed(2).padStart(7);
      lines.push(txt(`${tax}${taxbl}${cgst}${sgst}${igst}`));
    });
  }

  if (receipt.notes) {
    lines.push(sep());
    lines.push(bold('Notes'));
    lines.push(...wrapLines(receipt.notes, W));
  }

  if ((receipt.terms || []).length > 0) {
    lines.push(sep());
    lines.push(bold('Terms & Conditions'));
    receipt.terms.forEach((term, i) => {
      lines.push(...wrapLines(`${i + 1}. ${term}`, W));
    });
  }

  pushBrandingFooter(lines, receipt);
  lines.push(feed(3));
  lines.push(cut());

  return lines;
}

// ── Exchange ───────────────────────────────────────────────────────────────────

export function buildExchangeReceiptLines(receipt) {
  const lines = [];
  const { date, time } = dateParts(receipt.completedAt);

  // Header
  if (receipt.storeName) lines.push({ type: 'text', text: receipt.storeName, bold: true, align: 'center', fontSize: 2 });
  if (receipt.organizationName) lines.push(txt(receipt.organizationName, false, 'center'));
  if (receipt.storePhone) lines.push(txt(`Tel: ${receipt.storePhone}`, false, 'center'));
  if (receipt.gstin) lines.push(txt(`GST: ${receipt.gstin}`, false, 'center'));

  lines.push(sep());
  lines.push(bold('EXCHANGE RECEIPT', 'center'));
  lines.push(sep());

  if (receipt.exchangeNumber) lines.push(txt(twoCol('Exchange No', receipt.exchangeNumber)));
  if (receipt.originalOrderNumber) lines.push(txt(twoCol('Orig Order', receipt.originalOrderNumber)));
  lines.push(txt(twoCol('Date', date)));
  lines.push(txt(twoCol('Time', time)));
  if (receipt.cashierName) lines.push(txt(twoCol('Cashier', receipt.cashierName)));

  if (receipt.customerName || receipt.customerMobile) {
    lines.push(sep());
    if (receipt.customerName)   lines.push(txt(`Customer: ${receipt.customerName}`));
    if (receipt.customerMobile) lines.push(txt(`Mobile: ${receipt.customerMobile}`));
  }

  // Return side
  lines.push(sep());
  lines.push(bold('--- RETURN / CREDIT NOTE ---', 'center'));
  if (receipt.returnNumber)     lines.push(txt(twoCol('Return No', receipt.returnNumber)));
  if (receipt.creditNoteNumber) lines.push(txt(twoCol('Credit Note', receipt.creditNoteNumber)));

  const exShowRate = receipt.showRateColumn !== false;
  const exDecimals = receipt.itemAmountDecimalPlaces || 2;
  const exRateLabel = String(receipt.rateColumnLabel || 'Rate').substring(0, 6).padStart(6);

  lines.push(txt(`Sr Item                    Qty${exShowRate ? ` ${exRateLabel}` : ''} Refund`));
  (receipt.returnItems || []).forEach((item) => {
    const idx  = String(item.index).padEnd(3);
    const name = String(item.name).substring(0, 18).padEnd(19);
    const qty  = String(item.quantity).padStart(3);
    const rate = exShowRate ? parseFloat(item.rate || 0).toFixed(exDecimals).padStart(6) : null;
    const amt  = parseFloat(item.amount || 0).toFixed(exDecimals).padStart(7);
    // See buildReceiptLines: explicit separators guarantee a gap even when
    // a value exactly fills (or exceeds) its padStart target width. Qty is
    // capped at 100 (never decimal), so it only needs a 3-char target.
    lines.push(txt([ `${idx}${name}${qty}`, rate, amt ].filter((part) => part !== null).join(' ')));
    if (item.reason) lines.push(txt(`   (${item.reason})`));
  });
  lines.push(bold(twoCol('RETURN TOTAL', money(receipt.returnTotal))));
  if (receipt.refundMethod) lines.push(txt(twoCol('Refund via', receipt.refundMethod)));

  // New sale side
  lines.push(sep());
  lines.push(bold('--- NEW SALE / TAX INVOICE ---', 'center'));
  if (receipt.newInvoiceNumber) lines.push(txt(twoCol('Invoice', receipt.newInvoiceNumber)));

  const newShowAmt = receipt.showAmountColumn !== false;
  const newAmtLabel = String(receipt.amountColumnLabel || 'Amt').substring(0, 7).padStart(7);

  lines.push(txt(`Sr Item                    Qty${exShowRate ? ` ${exRateLabel}` : ''}${newShowAmt ? ` ${newAmtLabel}` : ''}`));
  (receipt.newItems || []).forEach((item) => {
    const idx  = String(item.index).padEnd(3);
    const name = String(item.name).substring(0, 18).padEnd(19);
    const qty  = String(item.quantity).padStart(3);
    const rate = exShowRate ? parseFloat(item.rate || 0).toFixed(exDecimals).padStart(6) : null;
    const amt  = newShowAmt ? parseFloat(item.amount || 0).toFixed(exDecimals).padStart(7) : null;
    // See buildReceiptLines: explicit separators guarantee a gap even when
    // a value exactly fills (or exceeds) its padStart target width. Qty is
    // capped at 100 (never decimal), so it only needs a 3-char target.
    lines.push(txt([ `${idx}${name}${qty}`, rate, amt ].filter((part) => part !== null).join(' ')));
  });
  lines.push(bold(twoCol('NEW SALE TOTAL', money(receipt.newSaleTotal))));

  (receipt.payments || []).forEach((p) => {
    lines.push(txt(twoCol(`  Payment (${p.label})`, money(p.amount))));
  });

  // Settlement
  lines.push(sep());
  lines.push(bold('SETTLEMENT SUMMARY', 'center'));
  lines.push(txt(twoCol('Return value', money(receipt.returnTotal))));
  lines.push(txt(twoCol('New sale', money(receipt.newSaleTotal))));
  lines.push(sep());

  const dir = receipt.settlementDirection;
  if (dir === 'even') {
    lines.push(bold('Even exchange — no extra payment', 'center'));
  } else if (dir === 'customer_refund') {
    lines.push(bold(twoCol('Refund issued', money(receipt.netSettlementAmount))));
  } else {
    lines.push(bold(twoCol('Customer paid', money(receipt.netSettlementAmount))));
  }

  pushBrandingFooter(lines, receipt);
  lines.push(feed(3));
  lines.push(cut());

  return lines;
}
